"use client";
import PageLink from "@/app/components/paginationLink";
import { FunctionComponent, useMemo } from "react";
import style from "./pagination.module.scss";

const Pagination: FunctionComponent<PaginationProps> = ({
  currentPage,
  totalPages,
  totalResults,
  baseUrl,
  shouldPrefetch = true,
  useQueryParamPagination = false,
  onPageChange,
}) => {
  const additionalPages = useMemo<number[]>(() => {
    const returnArray: number[] = [];
    let startingNumber = 0;
    let endingNumber = 0;
    const defaultClickablePages = 8;
    const defautClickablePagesInOneDirection = defaultClickablePages / 2;
    const totalClickablePages = Math.min(totalPages, defaultClickablePages);

    if (totalPages > 2 && currentPage <= totalClickablePages - 1) {
      startingNumber = 2;
      endingNumber =
        currentPage +
        Math.min(
          Math.max(Math.abs(currentPage - totalClickablePages), defautClickablePagesInOneDirection),
          totalClickablePages,
        );
    } else if (totalPages > totalClickablePages && currentPage >= totalPages - (totalClickablePages - 2)) {
      startingNumber =
        currentPage -
        Math.min(
          Math.max(Math.abs(totalPages - currentPage - totalClickablePages), defautClickablePagesInOneDirection),
          totalClickablePages,
        );
      endingNumber = totalPages - 1;
    } else if (currentPage >= totalClickablePages || currentPage <= totalPages - totalClickablePages) {
      startingNumber = currentPage - defautClickablePagesInOneDirection;
      endingNumber = currentPage + defautClickablePagesInOneDirection;
    }
    startingNumber = Math.max(startingNumber, 2);
    endingNumber = Math.min(endingNumber, totalPages - 1);
    if (startingNumber > 1) {
      for (let index = startingNumber; index <= endingNumber; index++) {
        returnArray.push(index);
      }
    }
    return returnArray;
  }, [currentPage, totalPages]);

  if (totalResults === 0) {
    return null;
  }

  const showLeadingEllipsis = additionalPages.length > 0 && additionalPages[0] > 2;
  const showTrailingEllipsis =
    additionalPages.length > 0 && additionalPages[additionalPages.length - 1] < totalPages - 1;

  if (onPageChange) {
    return (
      <nav aria-label="Pagination" className={style.Pagination}>
        <button
          className={`${style.navButton} ${currentPage === 1 ? style.navButtonDisabled : ""}`}
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <span className={`${style.caret} ${style.caretLeft}`} />
          Previous
        </button>

        <div className={style.pageNumbers}>
          <button
            className={currentPage === 1 ? style.currentPage : style.pageLink}
            type="button"
            onClick={() => onPageChange(1)}
            aria-current={currentPage === 1 ? "page" : undefined}
          >
            1
          </button>

          {showLeadingEllipsis && <span className={style.ellipsis}>...</span>}

          {additionalPages.map((pageNumber) => (
            <button
              key={pageNumber}
              className={currentPage === pageNumber ? style.currentPage : style.pageLink}
              type="button"
              onClick={() => onPageChange(pageNumber)}
              aria-current={currentPage === pageNumber ? "page" : undefined}
            >
              {pageNumber}
            </button>
          ))}

          {showTrailingEllipsis && <span className={style.ellipsis}>...</span>}

          {totalPages !== 1 && (
            <button
              className={currentPage === totalPages ? style.currentPage : style.pageLink}
              type="button"
              onClick={() => onPageChange(totalPages)}
              aria-current={currentPage === totalPages ? "page" : undefined}
            >
              {totalPages}
            </button>
          )}
        </div>

        <div className={style.mobilePageInfo}>
          {currentPage} out of {totalPages}
        </div>

        <button
          className={`${style.navButton} ${currentPage >= totalPages ? style.navButtonDisabled : ""}`}
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
        >
          Next
          <span className={`${style.caret} ${style.caretRight}`} />
        </button>
      </nav>
    );
  }

  return (
    <nav aria-label="Pagination" className={style.Pagination}>
      <PageLink
        pageNumber={currentPage - 1}
        currentPage={currentPage}
        baseUrl={baseUrl}
        disabled={currentPage === 1}
        shouldPrefetch={shouldPrefetch}
        useQueryParamPagination={useQueryParamPagination}
        variant="nav"
      >
        Previous
      </PageLink>

      <div className={style.pageNumbers}>
        <PageLink
          currentPage={currentPage}
          pageNumber={1}
          baseUrl={baseUrl}
          shouldPrefetch={shouldPrefetch}
          useQueryParamPagination={useQueryParamPagination}
        >
          {"1"}
        </PageLink>

        {showLeadingEllipsis && <span className={style.ellipsis}>...</span>}

        {additionalPages.map((pageNumber) => (
          <PageLink
            key={pageNumber}
            currentPage={currentPage}
            pageNumber={pageNumber}
            baseUrl={baseUrl}
            shouldPrefetch={shouldPrefetch}
            useQueryParamPagination={useQueryParamPagination}
          >
            {pageNumber.toString()}
          </PageLink>
        ))}

        {showTrailingEllipsis && <span className={style.ellipsis}>...</span>}

        {totalPages !== 1 && (
          <PageLink
            currentPage={currentPage}
            pageNumber={totalPages}
            baseUrl={baseUrl}
            shouldPrefetch={shouldPrefetch}
            useQueryParamPagination={useQueryParamPagination}
          >
            {totalPages.toString()}
          </PageLink>
        )}
      </div>

      <div className={style.mobilePageInfo}>
        {currentPage} out of {totalPages}
      </div>

      <PageLink
        pageNumber={currentPage + 1}
        currentPage={currentPage}
        baseUrl={baseUrl}
        disabled={currentPage >= totalPages}
        shouldPrefetch={shouldPrefetch}
        useQueryParamPagination={useQueryParamPagination}
        variant="nav"
      >
        Next
      </PageLink>
    </nav>
  );
};

type PaginationBase = {
  currentPage: number;
  totalPages: number;
  totalResults: number;
};

type LinkPaginationProps = PaginationBase & {
  baseUrl: string;
  shouldPrefetch?: boolean;
  useQueryParamPagination?: boolean;
  onPageChange?: never;
};

type CallbackPaginationProps = PaginationBase & {
  onPageChange: (page: number) => void;
  baseUrl?: never;
  shouldPrefetch?: never;
  useQueryParamPagination?: never;
};

export type PaginationProps = LinkPaginationProps | CallbackPaginationProps;

export default Pagination;
